/**
 * Sheet Extensions Constants
 *
 * Centralized configuration for sheet extension components to avoid magic numbers
 * and improve maintainability.
 */

// Z-index values for proper layering
export const SHEET_EXTENSIONS_CONSTANTS = {
  // Z-index for dropdown menus to ensure they appear above other content
  DROPDOWN_Z_INDEX: 'z-[1000]',

  // Z-index for tooltips
  TOOLTIP_Z_INDEX: 'z-[1000]',

  // Default button sizes
  BUTTON_SIZES: {
    SMALL: 'sm',
    DEFAULT: 'default',
    LARGE: 'lg',
  },

  // Default button variants
  BUTTON_VARIANTS: {
    DEFAULT: 'default',
    OUTLINE: 'outline',
    GHOST: 'ghost',
  },

  // Default badge variants
  BADGE_VARIANTS: {
    DEFAULT: 'default',
    SECONDARY: 'secondary',
    DESTRUCTIVE: 'destructive',
    OUTLINE: 'outline',
  },

  // Icon sizes for action menu items
  ICON_SIZES: {
    SMALL: 'h-3 w-3',
    MEDIUM: 'h-3.5 w-3.5',
    LARGE: 'h-4 w-4',
  },

  // Animation durations
  ANIMATION_DURATIONS: {
    FAST: 'duration-150',
    NORMAL: 'duration-200',
    SLOW: 'duration-300',
  },

  // Spacing values
  SPACING: {
    GAP_1: 'gap-1',
    GAP_2: 'gap-2',
    GAP_3: 'gap-3',
    GAP_4: 'gap-4',
  },

  // Border radius values
  BORDER_RADIUS: {
    SMALL: 'rounded-md',
    MEDIUM: 'rounded-lg',
    LARGE: 'rounded-xl',
  },
} as const;

// Default values for components
export const DEFAULT_VALUES = {
  // Default trigger text for action menus
  ACTION_MENU_TRIGGER_TEXT: 'Actions',

  // Default button size for action menus
  ACTION_MENU_BUTTON_SIZE: 'sm',

  // Default button variant for action menus
  ACTION_MENU_BUTTON_VARIANT: 'outline',

  // Default badge variant for date badges
  DATE_BADGE_VARIANT: 'secondary',

  // Default date format options
  DATE_FORMAT_OPTIONS: {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  } as const,
} as const;

// Type helpers
export type ButtonSize = typeof SHEET_EXTENSIONS_CONSTANTS.BUTTON_SIZES[keyof typeof SHEET_EXTENSIONS_CONSTANTS.BUTTON_SIZES];
export type ButtonVariant = typeof SHEET_EXTENSIONS_CONSTANTS.BUTTON_VARIANTS[keyof typeof SHEET_EXTENSIONS_CONSTANTS.BUTTON_VARIANTS];
export type BadgeVariant = typeof SHEET_EXTENSIONS_CONSTANTS.BADGE_VARIANTS[keyof typeof SHEET_EXTENSIONS_CONSTANTS.BADGE_VARIANTS];
